import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { API_BASE_URL } from "utils/api";
import { stripPortalBasePath } from "utils/portalPath";

const breadcrumbMap = {
  "lead-management": ["Lead Management"],
  "lead-management/lead-profile": ["Lead Management", "Lead Details"],
  "client-management": ["Client Management"],
  "staff-management": ["Staff Management"],
  "client-management/client-profile": ["Client Management", "Client Details"],
  "folders": ["Folders"],
  "folders/leads": ["Lead Folder"],
  "folders/clients": ["Client Folder"],
};

function humanize(segment) {
  if (!segment) return "";
  return segment
    .replace(/-/g, " ")
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export default function useBreadcrumbs() {
  const location = useLocation();
  const [items, setItems] = useState(["Main Dashboard"]);

  useEffect(() => {
    let mounted = true;
    const compute = async () => {
      const raw = stripPortalBasePath(location.pathname);
      const segments = raw.split("/").filter(Boolean);

      if (segments.length === 0) {
        if (mounted) setItems(["Main Dashboard"]);
        return;
      }

      // try exact map lookup (first two segments)
      const maybeKey = segments.slice(0, 2).join("/");
      if (breadcrumbMap[maybeKey] && segments.length <= 2) {
        if (mounted) setItems(breadcrumbMap[maybeKey]);
        return;
      }
      if (breadcrumbMap[segments[0]] && segments.length === 1) {
        if (mounted) setItems(breadcrumbMap[segments[0]]);
        return;
      }

      // lead-management detail: try state first, else fetch by id
      if (segments[0] === "lead-management") {
        if (segments.includes("lead-profile")) {
          // determine leadId: prefer state, else the segment after 'lead-profile'
          const stateLead = location.state?.lead;
          let leadId = stateLead?._id || location.state?.leadId;
          const idx = segments.indexOf("lead-profile");
          if (!leadId && idx >= 0) {
            const candidate = segments[idx + 1];
            // ignore literal 'undefined' or empty or 'lead-management'
            if (candidate && candidate !== "undefined" && candidate !== "lead-management") {
              leadId = candidate;
            }
          }

          // If we already have the lead name in state, use it.
          if (stateLead?.name) {
            if (mounted) setItems(["Lead Management", stateLead.name]);
            return;
          }

          // If we have an id, fetch lead metadata; show loading while fetching
          if (leadId) {
            if (mounted) setItems(["Lead Management", "Loading..."]);
            try {
              const token = localStorage.getItem("token");
              const res = await fetch(`${API_BASE_URL}/leads/${leadId}`, {
                headers: {
                  Authorization: token ? `Bearer ${token}` : undefined,
                  "Content-Type": "application/json",
                },
              });
              if (!res.ok) throw new Error("Lead not found");
              const json = await res.json();
              const lead = json?.data || json;
              const name = lead?.name ?? "Unknown Lead";
              if (mounted) setItems(["Lead Management", name]);
              return;
            } catch (err) {
              if (mounted) setItems(["Lead Management", "Unknown Lead"]);
              return;
            }
          }

          // no id and no state -> show generic Lead Management only
          if (mounted) setItems(["Lead Management"]);
          return;
        }
        if (mounted) setItems(["Lead Management"]);
        return;
      }

      // careof-management detail
      if (segments[0] === "careof-management") {
        if (segments.includes("profile")) {
          const stateCareOf = location.state?.careOf;
          let careOfName = stateCareOf?.name;
          const idx = segments.indexOf("profile");
          const candidateId = idx >= 0 ? segments[idx + 1] : null;

          if (!careOfName && candidateId && candidateId !== "undefined") {
            if (mounted) setItems(["CareOf Management", "Loading..."]);
            try {
              const token = localStorage.getItem("token");
              const res = await fetch(`${API_BASE_URL}/care-of/${encodeURIComponent(candidateId)}`, {
                headers: {
                  Authorization: token ? `Bearer ${token}` : undefined,
                  "Content-Type": "application/json",
                },
              });
              if (res.ok) {
                const json = await res.json();
                const careOf = json?.data || json;
                careOfName = careOf?.name || careOfName;
              }
            } catch (err) {
              // ignore fetch failures
            }
          }

          if (mounted) setItems(["CareOf Management", careOfName || "CareOf Details"]);
          return;
        }
        if (mounted) setItems(["CareOf Management"]);
        return;
      }

      // client-management detail
      if (segments[0] === "client-management") {
        if (segments.includes("client-profile")) {
          const stateClient = location.state?.client;
          let clientName = stateClient?.name || location.state?.lead?.name;
          const idx = segments.indexOf("client-profile");
          const candidateId = idx >= 0 ? segments[idx + 1] : null;

          if (!clientName && candidateId && candidateId !== "undefined") {
            if (mounted) setItems(["Client Management", "Loading..."]);
            try {
              const token = localStorage.getItem("token");
              const res = await fetch(`${API_BASE_URL}/clients/${candidateId}`, {
                headers: {
                  Authorization: token ? `Bearer ${token}` : undefined,
                  "Content-Type": "application/json",
                },
              });
              if (res.ok) {
                const json = await res.json();
                const client = json?.data || json;
                clientName = client?.name || clientName;
              }
            } catch (err) {
              // ignore fetch errors, fallback to generic label below
            }
          }

          if (mounted) setItems(["Client Management", clientName || "Client Details"]);
          return;
        }
        if (mounted) setItems(["Client Management"]);
        return;
      }

      // staff-management detail
      if (segments[0] === "staff-management") {
        if (segments.includes("edit") || segments.includes("permissions")) {
          const stateStaff = location.state?.staff;
          let staffName = stateStaff?.name;

          // determine id from path if state not present
          const idx = segments.includes("edit") ? segments.indexOf("edit") : segments.indexOf("permissions");
          const candidateId = idx >= 0 ? segments[idx + 1] : null;

          if (stateStaff?.name) {
            if (mounted) setItems(["Staff Management", stateStaff.name]);
            return;
          }

          if (!staffName && candidateId && candidateId !== "undefined") {
            if (mounted) setItems(["Staff Management", "Loading..."]);
            try {
              const token = localStorage.getItem("token");
              const res = await fetch(`${API_BASE_URL}/users/staff/${candidateId}`, {
                headers: {
                  Authorization: token ? `Bearer ${token}` : undefined,
                  "Content-Type": "application/json",
                },
              });
              if (res.ok) {
                const json = await res.json();
                const staff = json?.data || json;
                staffName = staff?.name || staffName;
              }
            } catch (err) {
              // ignore
            }
          }

          if (mounted) setItems(["Staff Management", staffName || "Staff Details"]);
          return;
        }
        if (mounted) setItems(["Staff Management"]);
        return;
      }

      // folders dynamic
      if (segments[0] === "folders") {
        if (segments.length === 1) {
          if (mounted) setItems(["Folders"]);
          return;
        }
        if (segments.length === 2 && segments[1] === "leads") {
          if (mounted) setItems(["Lead Folder"]);
          return;
        }
        if (segments.length === 2 && segments[1] === "clients") {
          if (mounted) setItems(["Client Folder"]);
          return;
        }

        // assume folders/:type/:folderId
        const typeSegment = segments[1];
        const folderId = segments[2];
        if (!folderId || !["leads", "clients"].includes(typeSegment)) {
          if (mounted) setItems(["Folders"]);
          return;
        }

        const typeLabel = typeSegment === "clients" ? "Client Folder" : "Lead Folder";
        const stateFolder = location.state?.folder;
        const stateType = location.state?.type || location.state?.folder?.type;
        const stateName = stateFolder?.name || location.state?.folderName;
        const stateTypeLabel = stateType
          ? String(stateType).toLowerCase() === "client"
            ? "Client Folder"
            : "Lead Folder"
          : typeLabel;

        if (stateName) {
          if (mounted) setItems([stateTypeLabel, stateName]);
          return;
        }

        if (mounted) setItems([typeLabel, "Loading..."]);

        try {
          const token = localStorage.getItem("token");
          const res = await fetch(`${API_BASE_URL}/folders/${folderId}`, {
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined,
              "Content-Type": "application/json",
            },
          });
          if (!res.ok) throw new Error("Folder not found");
          const result = await res.json();
          const folder = result?.data || result;
          const type = String(folder?.type || "").toLowerCase();
          const finalTypeLabel = type === "client" ? "Client Folder" : "Lead Folder";
          const name = folder?.name || "Unknown Folder";
          if (mounted) setItems([finalTypeLabel, name]);
          return;
        } catch (err) {
          if (mounted) setItems([typeLabel, "Unknown Folder"]);
          return;
        }
      }

      // fallback: humanize segments
      const sanitized = segments.map((s) => humanize(s)).filter(Boolean);
      if (mounted) setItems(sanitized.length > 0 ? sanitized : ["Main Dashboard"]);
    };

    compute();
    return () => {
      mounted = false;
    };
  }, [location.pathname, location.state]);

  // Remove duplicates and undefined, ensure no root duplication (root handled by Navbar)
  const cleaned = items
    .filter(Boolean)
    .map((it) => (typeof it === "string" ? it : String(it)))
    .filter((v, i, arr) => v && arr.indexOf(v) === i && v.toLowerCase() !== "mellbizz" && v.toLowerCase() !== "mellbizz");

  return cleaned;
}
